import type {
  AcademyOperatingProfileInput,
  OnboardingWorkflow,
  Plan,
  SubscriptionTier,
  ClubPlanTier,
  OrgOperationType,
  User,
} from '@court-sync/shared';
import {
  recommendSubscriptionTier,
  recommendClubPlan,
  resolveSubscriptionTierForDisplay,
  normalizeClubPlanTier,
  ACADEMY_BUNDLE_TIERS,
  CLUB_TIER_FOR_ACADEMY_BUNDLE,
  resolveAcademyBundleTier,
  starterUnavailableFeaturesFromReasons,
  suggestWorkflowsFromGoals,
  type PlanRecommendationResult,
  type ClubPlanRecommendationResult,
} from '@court-sync/shared';
import type { OnboardingDraft } from './onboardingDraft';

export { ACADEMY_BUNDLE_TIERS, CLUB_TIER_FOR_ACADEMY_BUNDLE };

export const OPERATING_ROLE_LABELS = {
  solo_coach: 'Solo coach — I run everything myself',
  owner_coach: 'Owner-coach — I teach and manage other coaches',
  owner_manager: 'Owner-manager — I manage coaches who teach',
} as const;

export const ACADEMY_SIZE_LABELS = {
  '1-25': '1–25 students',
  '26-100': '26–100 students',
  '101-300': '101–300 students',
  '300+': '300+ students',
} as const;

export const COACH_COUNT_LABELS = {
  '0': 'Just me',
  '1': '1 other coach',
  '2-5': '2–5 coaches',
  '6+': '6 or more coaches',
} as const;

export const LOCATION_COUNT_LABELS = {
  '1': 'One location',
  '2-3': '2–3 locations',
  '4+': '4 or more locations',
} as const;

export const ONBOARDING_GOAL_LABELS = {
  reduce_admin: 'Reduce admin work',
  grow_enrollment: 'Grow enrollment',
  simplify_scheduling: 'Simplify scheduling',
  improve_communication: 'Improve communication',
  manage_payments: 'Collect lesson payments online',
  player_development: 'Player development tracking',
} as const;

export const ONBOARDING_WORKFLOW_LABELS = {
  group_programs: 'Group programs & terms',
  private_lessons: 'Private lessons',
  camps: 'Holiday camps',
  online_payments: 'Online card payments',
  waitlists: 'Waitlists when lessons are full',
  makeup_credits: 'Make-up credits',
  programs_pathways: 'Development programs & pathways',
  coach_attendance: 'Coach attendance tracking',
  multi_location: 'Multiple locations',
  coach_portals: 'Coach dashboards',
  student_portals: 'Student & guardian accounts',
  student_self_booking: 'Self-service booking',
} as const;

export function deriveOperatingProfileFlags(
  profile: Partial<AcademyOperatingProfileInput>
): Partial<AcademyOperatingProfileInput> {
  const workflows = workflowsForRecommendation(profile);
  return {
    ...profile,
    needsOnlinePayments:
      profile.needsOnlinePayments ??
      workflows.includes('online_payments'),
    coachesNeedPortalAccess:
      profile.coachesNeedPortalAccess ??
      workflows.includes('coach_portals'),
    studentsNeedAccounts:
      profile.studentsNeedAccounts ??
      workflows.includes('student_portals'),
    studentsInitiateBookings:
      profile.studentsInitiateBookings ??
      workflows.includes('student_self_booking'),
  };
}

function workflowsForRecommendation(
  profile: Partial<AcademyOperatingProfileInput>
): OnboardingWorkflow[] {
  const explicit = profile.desiredWorkflows ?? [];
  const implied = suggestWorkflowsFromGoals(profile);
  return [...new Set([...explicit, ...implied])];
}

export function buildPlanRecommendation(
  profile: Partial<AcademyOperatingProfileInput>
): PlanRecommendationResult {
  const derived = deriveOperatingProfileFlags(profile);
  return recommendSubscriptionTier({
    ...derived,
    desiredWorkflows: workflowsForRecommendation(profile),
  });
}

export function buildClubPlanRecommendation(
  profile: Partial<import('@court-sync/shared').ClubOperatingProfileInput>
): ClubPlanRecommendationResult {
  return recommendClubPlan(profile);
}

const SUBSCRIPTION_TIER_RANK: Record<SubscriptionTier, number> = {
  starter: 0,
  professional: 1,
  enterprise: 2,
};

/**
 * Resolve which academy plan tier should appear selected during onboarding.
 * Prefers the live questionnaire recommendation unless the operator explicitly
 * chose a different tier (Starter with acknowledgment, or a higher tier).
 */
export function resolveOnboardingSelectedPlanTier(
  draft: OnboardingDraft | null | undefined,
  recommendation: PlanRecommendationResult
): SubscriptionTier {
  const recommended = recommendation.recommendedTier;
  const selected = draft?.selectedPlanTier;

  if (draft?.planTierManuallySelected && selected) {
    return selected;
  }

  if (!selected) {
    return recommended;
  }

  if (selected === recommended) {
    return selected;
  }

  if (selected === 'starter' && recommended !== 'starter') {
    return draft?.starterLimitationsAcknowledged ? 'starter' : recommended;
  }

  if (SUBSCRIPTION_TIER_RANK[selected] > SUBSCRIPTION_TIER_RANK[recommended]) {
    return selected;
  }

  return recommended;
}

export function resolveFinishSignupRecommendedTier(
  draft: OnboardingDraft | null | undefined,
  academyProfile?: Partial<AcademyOperatingProfileInput> | null
): SubscriptionTier {
  const liveRecommendation = academyProfile
    ? buildPlanRecommendation(deriveOperatingProfileFlags(academyProfile))
    : null;

  if (liveRecommendation) {
    return resolveOnboardingSelectedPlanTier(draft, liveRecommendation);
  }

  if (draft?.recommendedTier) return draft.recommendedTier;
  return 'professional';
}

export function resolveOnboardingSelectedClubPlanTier(
  draft: OnboardingDraft | null | undefined,
  recommendation: ClubPlanRecommendationResult
): ClubPlanTier {
  const recommended = recommendation.recommendedTier;
  const selected = draft?.selectedClubPlanTier;

  if (draft?.clubPlanTierManuallySelected && selected) {
    return selected;
  }

  return selected ?? recommended;
}

/**
 * Combined org checkout pairs each academy tier with a fixed club line tier.
 * Club tier must follow the academy bundle mapping, not the standalone club recommendation.
 */
export function resolveOnboardingSelectedBundleTiers(
  draft: OnboardingDraft | null | undefined,
  academyRecommendation: PlanRecommendationResult
): { academyTier: SubscriptionTier; clubTier: ClubPlanTier } {
  const academyTier = resolveAcademyBundleTier(
    resolveOnboardingSelectedPlanTier(draft, academyRecommendation),
  );
  const clubTier = CLUB_TIER_FOR_ACADEMY_BUNDLE[academyTier];
  return { academyTier, clubTier };
}

export function shouldAutoCheckoutFromOnboarding(options: {
  returnedFromCheckoutCancel: boolean;
  orgSetupCompleted?: boolean;
  /** @deprecated Use orgSetupCompleted — kept for navigation state from older flows */
  academyCreated?: boolean;
}): boolean {
  const setupCompleted = options.orgSetupCompleted ?? options.academyCreated;
  return Boolean(setupCompleted) && !options.returnedFromCheckoutCancel;
}

export function resolveOnboardingCheckoutTiers(
  orgOperationType: OrgOperationType,
  draft: OnboardingDraft | null | undefined,
  academyRecommendation: PlanRecommendationResult,
  options?: {
    selectedAcademyTierFromState?: SubscriptionTier;
    selectedClubTierFromState?: ClubPlanTier;
  }
): { academyTier?: SubscriptionTier; clubTier?: ClubPlanTier } | null {
  if (orgOperationType === 'both') {
    const bundle = resolveOnboardingSelectedBundleTiers(draft, academyRecommendation);
    const academyTier = resolveAcademyBundleTier(
      options?.selectedAcademyTierFromState ?? bundle.academyTier,
    );
    return {
      academyTier,
      clubTier: CLUB_TIER_FOR_ACADEMY_BUNDLE[academyTier],
    };
  }

  if (orgOperationType === 'academy') {
    return {
      academyTier:
        options?.selectedAcademyTierFromState ??
        resolveOnboardingSelectedPlanTier(draft, academyRecommendation),
    };
  }

  if (orgOperationType === 'club') {
    return {
      clubTier:
        options?.selectedClubTierFromState ??
        resolveFinishSignupRecommendedClubTier(draft, draft?.clubOperatingProfile ?? {}),
    };
  }

  return null;
}

export function resolveFinishSignupRecommendedClubTier(
  draft: OnboardingDraft | null | undefined,
  clubProfile?: Partial<import('@court-sync/shared').ClubOperatingProfileInput> | null
): ClubPlanTier {
  const liveRecommendation = clubProfile ? buildClubPlanRecommendation(clubProfile) : null;

  if (liveRecommendation) {
    return resolveOnboardingSelectedClubPlanTier(draft, liveRecommendation);
  }

  if (draft?.recommendedClubTier) return draft.recommendedClubTier;
  return 'professional';
}

export function filterClubSubscriptionPlans(plans: Plan[]): Plan[] {
  return plans.filter((plan) => normalizeClubPlanTier(plan.internalId ?? null) != null);
}

export function inferOrgOperationTypeForCheckout(
  draft: OnboardingDraft | null | undefined,
  stateType: OrgOperationType | undefined,
  user: Pick<User, 'academyId' | 'roles' | 'rolesWithAcademy'> | null | undefined,
): OrgOperationType {
  if (stateType) return stateType;
  if (draft?.orgOperationType) return draft.orgOperationType;

  const roles = user?.rolesWithAcademy ?? [];
  const hasAcademyOwner =
    Boolean(user?.academyId) ||
    roles.some(
      (r) =>
        (r.role === 'academy_owner' || r.role === 'academy_admin') && r.academyId != null,
    );
  const hasClubOwner =
    roles.some(
      (r) => (r.role === 'club_owner' || r.role === 'club_admin') && r.clubId != null,
    ) ||
    (roles.some((r) => r.role === 'club_owner' || r.role === 'club_admin') && !hasAcademyOwner);

  if (hasAcademyOwner && hasClubOwner) {
    // Academy-only orgs get an internal shell club + club_owner role; treat as academy unless
    // onboarding explicitly chose the combined path.
    return draft?.orgOperationType === 'both' ? 'both' : 'academy';
  }
  if (hasClubOwner) return 'club';
  return 'academy';
}

export function resolveCheckoutOrganizationId(
  draft: OnboardingDraft | null | undefined,
  options?: {
    organizationIdFromState?: string | null;
    academyOrganizationId?: string | null;
  }
): string | undefined {
  const fromAcademy = options?.academyOrganizationId?.trim();
  if (fromAcademy) return fromAcademy;

  const fromState = options?.organizationIdFromState?.trim();
  if (fromState) return fromState;

  const fromDraft = draft?.organizationId?.trim();
  if (fromDraft) return fromDraft;

  return undefined;
}

export function resolveCheckoutPlanPreselection(
  planId: string | null | undefined,
  orgOperationType: OrgOperationType,
  draft: OnboardingDraft | null | undefined,
): string | null {
  if (!planId?.trim()) return null;
  const trimmed = planId.trim();

  if (orgOperationType === 'club') {
    return draft?.selectedClubPlanTier ?? trimmed;
  }
  if (orgOperationType === 'academy') {
    return draft?.selectedPlanTier ?? trimmed;
  }
  return trimmed;
}

export function findPlanByTier(plans: Plan[], tier: SubscriptionTier): Plan | undefined {
  return plans.find(
    (plan) => resolveSubscriptionTierForDisplay(plan.name, plan.internalId ?? null) === tier
  );
}

export function findClubPlanByTier(plans: Plan[], tier: ClubPlanTier): Plan | undefined {
  return plans.find(
    (plan) => (normalizeClubPlanTier(plan.internalId ?? null) ?? 'starter') === tier
  );
}

export function getRecommendedPlanFromList(
  plans: Plan[],
  draft: OnboardingDraft | null | undefined,
  academyProfile?: Partial<AcademyOperatingProfileInput> | null
): Plan | undefined {
  const tier = resolveFinishSignupRecommendedTier(draft, academyProfile);
  return findPlanByTier(plans, tier);
}

export function getStarterLimitationSummary(recommendation: PlanRecommendationResult): string[] {
  if (recommendation.recommendedTier === 'starter') return [];
  return starterUnavailableFeaturesFromReasons(recommendation.reasonCodes);
}

export function formatPlanTierLabel(tier: SubscriptionTier): string {
  if (tier === 'starter') return 'Starter';
  if (tier === 'professional') return 'Professional';
  return 'Enterprise';
}

export function formatClubPlanTierLabel(tier: ClubPlanTier): string {
  if (tier === 'starter') return 'Club';
  return 'Club Pro';
}
